import enviro from 'enviro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LanguageNames from 'ui-addon-i18n/constants/LanguageNames';
import { PUBLIC_LANGUAGES } from '../../constants/Languages';
import { getFullPublicUrl, getPublicLocalizedPathname } from '../../utils/urls';
import { getMarketingEmbedNavLocale } from './MarketingNav.utils';

export function useMarketingNavQa(_useMarketingNavCdnQa = false) {
  // eslint-disable-next-line react-hooks/refs
  return useRef(
    enviro.isQa() &&
      (_useMarketingNavCdnQa ||
        /_marketingNavCdnQa=true/i.test(window.location.search) ||
        /_marketingEmbedNavQa=true/i.test(window.location.search))
  ).current;
}

interface MarketingEmbedNavLanguage {
  locale: string;
  name: string;
  url: string;
}

export function useMarketingEmbedNavLanguages({
  location,
}: {
  location: {
    pathname: string;
    search: string;
  };
}): MarketingEmbedNavLanguage[] {
  const { pathname, search } = location;

  return useMemo(() => {
    return PUBLIC_LANGUAGES.reduce((acc, lang) => {
      if (lang in LanguageNames) {
        const localizedPathname = `${getPublicLocalizedPathname(
          lang
        )}${pathname.replace(
          /^\/(de|es|fr|it|ja|nl|fi|da|no|pl|sv|pt|zh-tw|zh-cn|zh|th|ko)\/?/,
          ''
        )}`.replace(/\/$/, '');

        const languageName = LanguageNames[lang];

        acc.push({
          locale: getMarketingEmbedNavLocale(lang),
          name: languageName,
          url: `${getFullPublicUrl()}${localizedPathname}${search}`,
        });
      }
      return acc;
    }, [] as MarketingEmbedNavLanguage[]);
  }, [pathname, search]);
}

export function useLoadMarketingEmbedNavAsset({
  assetId,
  assetType,
  assetUrl,
  initialLoading = true,
}: {
  assetId: string;
  assetType: 'script' | 'css';
  assetUrl: string;
  initialLoading?: boolean;
}) {
  const [{ loading, error }, setState] = useState<{
    loading: boolean;
    error: Error | null;
  }>({
    loading: initialLoading,
    error: null,
  });

  const addEventListeners = useCallback((element: HTMLElement) => {
    const handleLoad = () => {
      element.setAttribute('data-loaded', 'true');
      setState({
        loading: false,
        error: null,
      });
    };
    const handleError = (event: ErrorEvent) => {
      setState({
        loading: false,
        error: event.error,
      });
    };
    element.addEventListener('load', handleLoad);
    element.addEventListener('error', handleError);
    return {
      removeEventListeners: () => {
        element.removeEventListener('load', handleLoad);
        element.removeEventListener('error', handleError);
      },
    };
  }, []);

  const setLoaded = useCallback(() => {
    setState({
      loading: false,
      error: null,
    });
  }, []);

  useEffect(() => {
    if (!loading) {
      return;
    }
    const existingAsset = document.getElementById(assetId);
    if (existingAsset) {
      if (existingAsset.hasAttribute('data-loaded')) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setLoaded();
        return;
      }
      addEventListeners(existingAsset);
      return;
    }
    let asset: HTMLLinkElement | HTMLScriptElement;
    if (assetType === 'css') {
      asset = document.createElement('link');
      asset.href = assetUrl;
      asset.rel = 'stylesheet';
    } else {
      asset = document.createElement('script');
      asset.src = assetUrl;
    }
    asset.id = assetId;
    const { removeEventListeners } = addEventListeners(asset);
    document.head.appendChild(asset);
    return () => {
      removeEventListeners();
    };
  }, [addEventListeners, assetId, assetType, assetUrl, loading, setLoaded]);

  useEffect(() => {
    return () => {
      const asset = document.getElementById(assetId);
      if (asset) {
        asset.remove();
      }
    };
  }, [assetId]);

  return {
    loading,
    error,
  };
}
